import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import { Box, Checkbox, IconButton, Tooltip, Typography } from "@mui/material";
import type { ICellRendererParams } from "ag-grid-community";
import { EDIT_ICON_SX, TEXT } from "@/controls/section-panel/SectionPanel";
import { mesfColors } from "@/setup/ag-grid/mesf-theme";
import { OPERATIONAL_WRITES_LOCKED_MESSAGE } from "@/shared/hooks/useOperationalWritesLocked";
import { COLORS } from "../models/constants";
import type { StandData } from "../models/StandData";

const outOfRangeSx = { color: COLORS.outOfRange, fontWeight: "bold" } as const;
const checkboxSx = { p: 0, "& .MuiSvgIcon-root": { fontSize: 18 } } as const;
const editBtnSx = { p: 0, ml: 0.25 } as const;
const cellFontSx = {
	...TEXT.dense,
	lineHeight: "26px",
	fontVariantNumeric: "tabular-nums",
} as const;
const secondaryStyle = {
	fontSize: TEXT.secondary.fontSize,
	opacity: 0.6,
} as const;
const wrapCellSx = {
	fontSize: TEXT.secondary.fontSize,
	lineHeight: 1.2,
	whiteSpace: "normal" as const,
	wordBreak: "break-word" as const,
} as const;

// Edit pencil of a clickable cell; greyed with the pre-cutover tooltip when locked.
const EditIcon = ({ locked, sx }: { locked: boolean; sx?: object }) =>
	locked ? (
		<Tooltip title={OPERATIONAL_WRITES_LOCKED_MESSAGE}>
			<EditOutlinedIcon
				sx={{ ...EDIT_ICON_SX, ml: 0.25, ...sx, color: "action.disabled" }}
			/>
		</Tooltip>
	) : (
		<EditOutlinedIcon sx={{ ...EDIT_ICON_SX, ml: 0.25, ...sx }} />
	);

export const StandCellRenderer = (params: ICellRendererParams) => {
	const stand = params.value as StandData | undefined;
	if (!stand) return null;
	const writeDisabled = !!params.context?.writeDisabled;

	const propertyKey = params.data?.propertyKey as string;
	const enableComp = stand.EnableRecipeComparison;

	switch (propertyKey) {
		case "selWork": {
			const mismatch = enableComp && stand.Selected !== stand.SelectedRecipe;
			const selTooltip = mismatch
				? `Selected (${stand.Selected ? "Yes" : "No"}) does not match Recipe (${stand.SelectedRecipe ? "Yes" : "No"})`
				: undefined;
			return (
				<Box
					title={selTooltip}
					sx={{
						display: "flex",
						justifyContent: "center",
						alignItems: "center",
						gap: 0.25,
						height: "100%",
						...(mismatch ? outOfRangeSx : {}),
					}}
				>
					<Checkbox
						checked={stand.Selected}
						size="small"
						disabled
						sx={checkboxSx}
					/>
					<Typography sx={{ fontSize: TEXT.secondary.fontSize, mx: 0.25 }}>
						-
					</Typography>
					<Checkbox
						checked={stand.Working}
						size="small"
						disabled
						sx={checkboxSx}
					/>
				</Box>
			);
		}

		case "tc": {
			if (!stand.HasTensionControl) return null;
			return (
				<Box
					sx={{
						display: "flex",
						justifyContent: "center",
						alignItems: "center",
						height: "100%",
					}}
				>
					<Checkbox
						checked={stand.TensionControl}
						size="small"
						disabled
						sx={checkboxSx}
					/>
				</Box>
			);
		}

		case "cartridge": {
			const canEdit = !!stand.HasRollset;
			const text =
				stand.CartridgeName || (stand.HasRollset ? "No cartridge" : "");
			const noCartridge = stand.HasRollset && !stand.CartridgeName;
			return (
				<Box
					onClick={
						canEdit && !writeDisabled
							? () => params.context?.onChangeRollset?.(stand)
							: undefined
					}
					sx={{
						display: "flex",
						alignItems: "center",
						justifyContent: "center",
						width: "100%",
						height: "100%",
						overflow: "hidden",
						minWidth: 0,
					}}
				>
					<Typography
						noWrap
						title={text}
						sx={{
							...cellFontSx,
							flex: 1,
							textAlign: "center",
							minWidth: 0,
							...(noCartridge ? { color: "text.secondary" } : {}),
						}}
					>
						{text}
					</Typography>
					{canEdit && <EditIcon locked={writeDisabled} />}
				</Box>
			);
		}

		case "barrel": {
			const text = stand.BarrelName || (stand.HasRollset ? "No barrel" : "");
			return (
				<Box
					sx={{
						display: "flex",
						alignItems: "center",
						justifyContent: "center",
						width: "100%",
						height: "100%",
						overflow: "hidden",
						minWidth: 0,
					}}
				>
					<Typography
						title={text}
						sx={{ ...wrapCellSx, flex: 1, textAlign: "center", minWidth: 0 }}
					>
						{text}
					</Typography>
				</Box>
			);
		}

		case "pass": {
			const mismatch =
				stand.PassId != null && !stand.PassMatchWithProductMillSetup;
			const canEdit = !!(
				stand.HasPass &&
				stand.PassName &&
				stand.CanChangePass
			);
			const hasValidPass =
				stand.PassPosition != null && stand.PassPosition > 0 && stand.PassName;
			const display = hasValidPass
				? `${stand.PassPosition}-${stand.PassName}`
				: stand.HasPass
					? "No pass"
					: "";

			// Tooltip: full info + expected pass when mismatched
			let tooltip = hasValidPass
				? `Position: ${stand.PassPosition} | Name: ${stand.PassName}`
				: "No pass";
			if (!stand.PassMatchWithProductMillSetup) {
				tooltip += ` | Expected: ${stand.ProductMillSetupPassName || "N/A"}`;
			}

			return (
				<Box
					onClick={
						canEdit && !writeDisabled
							? () => params.context?.onChangePass?.(stand)
							: undefined
					}
					sx={{
						display: "flex",
						alignItems: "center",
						justifyContent: "center",
						width: "100%",
						height: "100%",
						overflow: "hidden",
						minWidth: 0,
						...(mismatch ? outOfRangeSx : {}),
					}}
				>
					<Typography
						title={tooltip}
						sx={{
							...wrapCellSx,
							flex: 1,
							textAlign: "center",
							color: "inherit",
							fontWeight: "inherit",
							minWidth: 0,
						}}
					>
						{display}
					</Typography>
					{canEdit && (
						<EditIcon
							locked={writeDisabled}
							sx={mismatch ? { color: "inherit" } : undefined}
						/>
					)}
				</Box>
			);
		}

		case "rollDiam": {
			const rollDiameter =
				Math.max(stand.RollDiameterBottom ?? 0, stand.RollDiameterTop ?? 0) ||
				null;
			const rollDiameterMatch =
				rollDiameter == null ||
				stand.RollDiameterL1 == null ||
				stand.RollDiameterL1 === 0 ||
				Math.abs(rollDiameter - stand.RollDiameterL1) <= 0.005;
			const isOutOfRange = enableComp && !rollDiameterMatch;

			if (rollDiameter == null) return null;

			const diamTooltip = isOutOfRange
				? `Roll diameter (${rollDiameter.toFixed(3)}) does not match L1 value (${stand.RollDiameterL1?.toFixed(3) ?? "N/A"})`
				: `Roll diameter: ${rollDiameter.toFixed(3)}`;

			return (
				<Box
					title={diamTooltip}
					sx={{
						textAlign: "center",
						...cellFontSx,
						...(isOutOfRange ? outOfRangeSx : {}),
					}}
				>
					<span style={{ fontWeight: "bold" }}>{rollDiameter.toFixed(3)}</span>
					{stand.RollDiameterL1 != null &&
						(!rollDiameterMatch || stand.RollDiameterL1 === 0) && (
							<span
								style={{
									color:
										stand.RollDiameterL1 === 0
											? mesfColors.grey[400]
											: COLORS.outOfRange,
									marginLeft: 3,
									fontSize: TEXT.secondary.fontSize,
								}}
							>
								{stand.RollDiameterL1.toFixed(3)}
							</span>
						)}
				</Box>
			);
		}

		case "rollGroove": {
			const mismatch =
				enableComp &&
				stand.RollGroove != null &&
				stand.RollGrooveRecipe != null &&
				Number(stand.RollGroove.toFixed(2)) !==
					Number(stand.RollGrooveRecipe.toFixed(2));
			const grooveTooltip = mismatch
				? `Roll groove (${stand.RollGroove?.toFixed(2)}) does not match Recipe (${stand.RollGrooveRecipe?.toFixed(2)})`
				: undefined;
			return (
				<Box
					title={grooveTooltip}
					sx={{
						textAlign: "center",
						...cellFontSx,
						...(mismatch ? outOfRangeSx : {}),
					}}
				>
					{stand.RollGroove != null ? stand.RollGroove.toFixed(2) : ""}
				</Box>
			);
		}

		case "grooveRecipe":
			return (
				<Box
					sx={{ textAlign: "center", ...cellFontSx, color: "text.secondary" }}
				>
					{stand.RollGrooveRecipe != null
						? stand.RollGrooveRecipe.toFixed(2)
						: ""}
				</Box>
			);

		case "passCount": {
			const over =
				enableComp &&
				stand.PassCountABB != null &&
				stand.PassCountABBMax != null &&
				stand.PassCountABB > stand.PassCountABBMax;
			const countTooltip = over
				? `Pass count (${Math.round(stand.PassCountABB!)}) exceeds maximum (${Math.round(stand.PassCountABBMax!)})`
				: undefined;
			return (
				<Box
					title={countTooltip}
					sx={{
						textAlign: "center",
						...cellFontSx,
						...(over ? outOfRangeSx : {}),
					}}
				>
					{stand.PassCountABB != null && (
						<>
							<span style={{ fontWeight: "bold" }}>
								{Math.round(stand.PassCountABB)}
							</span>
							{stand.PassCountABBMax != null && (
								<span style={secondaryStyle}>
									{" "}
									/ {Math.round(stand.PassCountABBMax)}
								</span>
							)}
						</>
					)}
				</Box>
			);
		}

		case "rFactor": {
			const isOut =
				enableComp &&
				stand.RFactorCurrent != null &&
				((stand.RFactorMin != null &&
					stand.RFactorCurrent < stand.RFactorMin) ||
					(stand.RFactorMax != null &&
						stand.RFactorCurrent > stand.RFactorMax));

			if (stand.RFactorCurrent == null) return null;

			const rFactorTooltip = isOut
				? `R-Factor (${stand.RFactorCurrent.toFixed(3)}) is outside Recipe range (${stand.RFactorMin?.toFixed(3) ?? "?"} - ${stand.RFactorMax?.toFixed(3) ?? "?"})`
				: undefined;

			return (
				<Box
					title={rFactorTooltip}
					sx={{
						textAlign: "center",
						lineHeight: 1.3,
						display: "flex",
						flexDirection: "column",
						justifyContent: "center",
						height: "100%",
						...(isOut ? outOfRangeSx : {}),
					}}
				>
					<Box sx={{ ...TEXT.dense, fontVariantNumeric: "tabular-nums" }}>
						<span style={{ fontWeight: "bold" }}>
							{stand.RFactorCurrent.toFixed(3)}
						</span>
						{stand.RFactorTarget != null && (
							<span style={secondaryStyle}>
								{" "}
								/ {stand.RFactorTarget.toFixed(3)}
							</span>
						)}
					</Box>
					{(stand.RFactorMin != null || stand.RFactorMax != null) && (
						<Box sx={secondaryStyle}>
							{stand.RFactorMin?.toFixed(3) ?? "?"} -{" "}
							{stand.RFactorMax?.toFixed(3) ?? "?"}
						</Box>
					)}
				</Box>
			);
		}

		default:
			return null;
	}
};
